import React, { useCallback, useEffect, useRef } from 'react';
import HarbourIcon from './HarbourIcon';
import HarbourButton from './HarbourButton';
import './harbour.css';

/*
 * Harbour confirm dialog (Figma Harbour/Modal overlay, 3087:16928): a scrim
 * over the page with a centred 560px card — title with a ghost close, an
 * optional body, and Cancel plus the one primary action right-aligned.
 *
 * Accessibility follows the FamilyConsentPage sheet contract: focus moves to
 * the title on open, Escape and the scrim close it, and nothing closes while
 * the action is busy (the busy state says what is happening on the control).
 */
export default function HarbourModal({
  title,
  // A small line above the title naming what the dialog is about, for
  // example the pupil and their year group.
  eyebrow,
  children,
  confirmLabel = 'Confirm',
  // 'primary' (plum) on SENCO surfaces; 'family' (gold) when the dialog sits
  // on a parent or carer screen, where the one main action is gold.
  confirmKind = 'primary',
  // Off for a dialog whose one action is to close it (an information
  // dialog): the primary then reads "Close" and there is no Cancel beside it.
  showCancel = true,
  // Off for a dialog whose actions live in its body (a search that is
  // chosen from row by row): only Cancel is left in the footer.
  showConfirm = true,
  // The way out beside the action. The submit pop-up (5226:131473) draws it
  // as an outline "Check my answers first"; elsewhere it is a text Cancel,
  // or "Close" once the dialog has done its job.
  cancelLabel = 'Cancel',
  cancelKind = 'tertiary',
  // The submit pop-up has no close cross: its way out is the labelled
  // button. Escape and the scrim still close it.
  showClose = true,
  // A variant class on the overlay, e.g. the submit pop-up's phone sheet.
  className = '',
  onConfirm,
  onClose,
  busy = false,
}) {
  const titleRef = useRef(null);
  const close = useCallback(() => {
    if (!busy && typeof onClose === 'function') onClose();
  }, [busy, onClose]);

  // Focus moves to the title ONCE, on open — never again, or every re-render
  // (a keystroke in a body field, a busy flip) would steal focus mid-typing.
  useEffect(() => {
    if (titleRef.current) titleRef.current.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  return (
    <div
      className={['harbour-modal-overlay', className].filter(Boolean).join(' ')}
      onClick={close}
      data-testid="harbour-modal-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="harbour-modal-title"
        className="harbour-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="harbour-modal__header">
          <div className="harbour-modal__heading">
            {eyebrow && <p className="harbour-modal__eyebrow">{eyebrow}</p>}
            <h2 id="harbour-modal-title" ref={titleRef} tabIndex={-1} className="harbour-modal__title">
              {title}
            </h2>
          </div>
          {showClose && (
            <button
              type="button"
              className="harbour-modal__close"
              aria-label="Close"
              disabled={busy}
              onClick={close}
            >
              <HarbourIcon name="close" size={20} />
            </button>
          )}
        </div>
        {children && <div className="harbour-modal__body">{children}</div>}
        <div className="harbour-modal__actions">
          {showCancel && (
            <HarbourButton kind={cancelKind} disabled={busy} onClick={close}>
              {cancelLabel}
            </HarbourButton>
          )}
          {showConfirm && (
            <HarbourButton kind={confirmKind} busy={busy} disabled={busy} aria-busy={busy} onClick={onConfirm}>
              {confirmLabel}
            </HarbourButton>
          )}
        </div>
      </div>
    </div>
  );
}
